import { inject, Injectable } from '@angular/core';
import { AssignmentTypes, CONFIG_TOKEN, Plan, PlanTypes, ReferenceDataForPlans } from '@pxl/models/common';
import {
    AppointmentService,
    CommonService,
    LoginService,
    MedicationPlanService,
    MedicationService,
    PatientService,
    VeterinarianService,
} from '@pxl/models/contracts';
import {
    AssignmentDetailsBaseModel,
    AssignmentDetailsType,
    AssignmentGroupService as AssignmentGroupBackendService,
    AssignmentMedicationDetailsModel,
    AssignmentPatientDetailsModel,
    AssignmentSearchQueryType,
    AssignmentService as AssignmentBackendService,
    AssignmentTypeService as AssignmentTypeBackendService,
    AssignmentVeterinarianDetailsModel,
    IPlanSearchQueryModel,
    MedicationAssignmentSearchQueryModel,
    MedicationPlanSearchQueryModel,
    PlanSearchQueryType,
    PlanService as PlanBackendService,
    TimingPreference,
} from '@pxl/shared/generated';
import { firstValueFrom } from 'rxjs';
import { DefaultAssignmentPlanService } from './assignment-plan.service';

@Injectable()
export class DefaultMedicationPlanService extends DefaultAssignmentPlanService implements MedicationPlanService {
    protected readonly assignmentTypeBackendService = inject(AssignmentTypeBackendService);
    protected readonly assignmentBackendService = inject(AssignmentBackendService);
    protected readonly planBackendService = inject(PlanBackendService);
    protected readonly assignmentGroupBackendService = inject(AssignmentGroupBackendService);
    protected readonly loginService = inject(LoginService);
    protected readonly veterinarianService = inject(VeterinarianService);
    protected readonly patientService = inject(PatientService);
    protected readonly appointmentService = inject(AppointmentService);
    protected readonly medicationService = inject(MedicationService);
    protected readonly commonService = inject(CommonService);
    protected readonly config = inject(CONFIG_TOKEN);

    public getMedicationPickUpAssignment(medicationId: string) {
        const model: MedicationAssignmentSearchQueryModel = {
            medicationIds: [medicationId],
            searchQueryType: AssignmentSearchQueryType.Medications,
            assignmentTypeIds: [AssignmentTypes.MedicationPickUp],
        };

        return firstValueFrom(this.assignmentBackendService.searchAssignments(model, this.apiVersion, this.token));
    }

    public createMedicationPickUpAssignment(
        medicationId: string,
        medicationRouteOfAdministrationId: string,
        medicationUnitId: string,
        dateUtc: Date,
        patientId?: string,
        veterinarianId?: string,
    ) {
        const details: AssignmentDetailsBaseModel[] = [
            {
                medicationId: medicationId,
                typeIdentifier: AssignmentDetailsType.AssignmentMedicationDetails,
                medicationUnitId: medicationUnitId,
                routeOfAdministrationId: medicationRouteOfAdministrationId,
            } as AssignmentMedicationDetailsModel,
        ];

        if (patientId) {
            const patientDetails: AssignmentPatientDetailsModel = {
                patientId,
                typeIdentifier: AssignmentDetailsType.AssignmentPatientDetails,
            };
            details.push(patientDetails);
        }

        if (veterinarianId) {
            const vetDetails: AssignmentVeterinarianDetailsModel = {
                veterinarianId,
                typeIdentifier: AssignmentDetailsType.AssignmentVeterinarianDetails,
            };
            details.push(vetDetails);
        }

        return firstValueFrom(
            this.assignmentBackendService.createAssignment(
                {
                    typeId: AssignmentTypes.MedicationPickUp,
                    dateUtc: dateUtc?.toISOString(),
                    name: 'Medication Pickup',
                    details,
                    reminderTimeSettings: [
                        {
                            timingPreference: TimingPreference.Pre,
                            years: null,
                            months: null,
                            weeks: null,
                            days: null,
                            hours: null,
                            minutes: 0,
                        },
                    ],
                },
                this.apiVersion,
                this.token,
            ),
        );
    }

    public getMedicationPlansByPatientIds(
        patientIds?: string[],
        includeArchived = true,
        includeHidden = true,
        prefetchedReferences?: ReferenceDataForPlans,
    ): Promise<Plan[]> {
        return this.getPlans([PlanTypes.MedicationPlan], patientIds, includeArchived, includeHidden, prefetchedReferences);
    }

    public getMedicationPlansByMedicationIds(
        medicationIds: string[],
        includeArchived?: boolean,
        prefetchedReferences?: ReferenceDataForPlans,
    ): Promise<Plan[]> {
        const searchQuery: MedicationPlanSearchQueryModel = {
            searchQueryType: PlanSearchQueryType.MedicationPlans,
            medicationIds,
            includeArchived,
        };
        return this.searchPlans(searchQuery as IPlanSearchQueryModel, prefetchedReferences);
    }
}
